import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronDown, ChevronUp, Loader2, AlertCircle, Search, RefreshCw, Shield,
  SlidersHorizontal, X, ArrowRight, TrendingUp, TrendingDown, CheckCircle,
  XCircle, Upload, Lock, RotateCcw, DollarSign, Globe, Clock, User
} from 'lucide-react';
import { paymentsAPI } from '../services/api';
import { useLocale } from '../contexts/LocaleContext';

// ─── Mobile detection ─────────────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [is, setIs] = useState(window.innerWidth < bp);
  useEffect(() => {
    const h = () => setIs(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return is;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n, currency = 'USD') =>
  n != null
    ? Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + currency
    : null;

const actorLabel = (l) => l.actor_name || l.actor_email || `User #${l.performed_by}`;

const formatMethod = (key) => {
  if (!key) return 'unknown method';
  const map = {
    bank_transfer_global:      'Global Bank Transfer',
    bank_transfer_per_user:    'Bank Transfer (Per User)',
    bank_transfer_per_country: 'Bank Transfer (Per Country)',
    crypto_payments:           'Crypto Payment',
    payment_links:             'Payment Link',
  };
  return map[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// ─── Action config ────────────────────────────────────────────────────────────
function getActionConfig(action, entityType, t) {
  const isWithdrawal = entityType === 'withdrawal_request';

  if (action === 'created' && isWithdrawal) {
    return {
      icon: TrendingDown,
      color: 'var(--accent-warning)',
      label: t('auditLog.actionWithdrawalRequested'),
      summary: (l) => {
        const n = l.new_data || {}; const o = l.old_data || {};
        return `${actorLabel(l)} requested a withdrawal of ${fmt(n.amount, n.currency) || '—'}. Balance reduced from ${fmt(o.balance) || '—'} to ${fmt(n.balance) || '—'} pending review.`;
      },
    };
  }
  if (action === 'created') {
    return {
      icon: TrendingUp,
      color: 'var(--accent-info)',
      label: t('auditLog.actionDepositSubmitted'),
      summary: (l) => {
        const n = l.new_data || {};
        return `${actorLabel(l)} submitted a deposit request for ${fmt(n.amount, n.currency) || '—'} via ${formatMethod(n.pluginKey)}.`;
      },
    };
  }
  if (action === 'proof_uploaded') {
    return {
      icon: Upload,
      color: 'var(--accent-info)',
      label: t('auditLog.actionProofUploaded'),
      summary: (l) => `${actorLabel(l)} uploaded a payment proof document and submitted it for review.`,
    };
  }
  if (action === 'approved' && !isWithdrawal) {
    return {
      icon: CheckCircle,
      color: 'var(--accent-success)',
      label: t('auditLog.actionDepositApproved'),
      summary: (l) => {
        const o = l.old_data || {}; const n = l.new_data || {};
        return o.balance != null && n.balance != null
          ? `${actorLabel(l)} approved the deposit. Trader balance updated from ${fmt(o.balance, n.currency)} → ${fmt(n.balance, n.currency)}.`
          : `${actorLabel(l)} approved the deposit and credited the trader's account.`;
      },
    };
  }
  if (action === 'rejected' && !isWithdrawal) {
    return {
      icon: XCircle,
      color: 'var(--accent-danger)',
      label: t('auditLog.actionDepositRejected'),
      summary: (l) => {
        const notes = l.new_data?.review_notes;
        return notes
          ? `${actorLabel(l)} rejected the deposit. Reason: "${notes}".`
          : `${actorLabel(l)} rejected the deposit request.`;
      },
    };
  }
  if (action === 'approved' && isWithdrawal) {
    return {
      icon: CheckCircle,
      color: 'var(--accent-success)',
      label: t('auditLog.actionWithdrawalApproved'),
      summary: (l) => `${actorLabel(l)} approved the withdrawal request. Funds will be released to the trader.`,
    };
  }
  if (action === 'rejected' && isWithdrawal) {
    return {
      icon: XCircle,
      color: 'var(--accent-danger)',
      label: t('auditLog.actionWithdrawalRejected'),
      summary: (l) => {
        const n = l.new_data || {}; const o = l.old_data || {};
        return n.balance != null
          ? `${actorLabel(l)} rejected the withdrawal. Funds returned — balance restored from ${fmt(o.balance) || '—'} to ${fmt(n.balance) || '—'}.`
          : `${actorLabel(l)} rejected the withdrawal and returned the funds.`;
      },
    };
  }
  if (action === 'locked' || action === 'withdrawal_locked') {
    return {
      icon: Lock, color: 'var(--accent-warning)',
      label: t('auditLog.actionFundsLocked'),
      summary: () => t('auditLog.fundsLockedSummary'),
    };
  }
  if (action === 'reversal' || action === 'withdrawal_reversal') {
    return {
      icon: RotateCcw, color: 'var(--accent-info)',
      label: t('auditLog.actionFundsReturned'),
      summary: () => t('auditLog.fundsReturnedSummary'),
    };
  }
  return {
    icon: Shield, color: 'var(--text-tertiary)',
    label: action?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || t('common.actions'),
    summary: () => t('auditLog.administrativeAction'),
  };
}

// ─── Filter tabs ──────────────────────────────────────────────────────────────
function useTabs(t) {
  return [
    { key: 'all',         label: t('auditLog.allEvents')      },
    { key: 'deposits',    label: t('auditLog.depositsTab')    },
    { key: 'withdrawals', label: t('auditLog.withdrawalsTab') },
  ];
}

function matchesTab(l, tab) {
  if (tab === 'all') return true;
  if (tab === 'deposits')    return l.entity_type === 'deposit_request';
  if (tab === 'withdrawals') return l.entity_type === 'withdrawal_request';
  return true;
}

// ─── Role badge ───────────────────────────────────────────────────────────────
function RoleBadge({ role }) {
  const isAdmin = role?.includes('admin');
  return (
    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
      style={{
        backgroundColor: isAdmin ? 'color-mix(in srgb, var(--accent-primary) 15%, transparent)' : 'color-mix(in srgb, var(--accent-info) 15%, transparent)',
        color: isAdmin ? 'var(--accent-primary)' : 'var(--accent-info)',
      }}>
      {isAdmin ? '🛡 Admin' : '👤 Trader'}
    </span>
  );
}

// ─── Balance change ───────────────────────────────────────────────────────────
function BalanceChange({ oldData, newData }) {
  const before = oldData?.balance;
  const after  = newData?.balance;
  if (before == null && after == null) return null;
  const diff = (before != null && after != null) ? parseFloat(after) - parseFloat(before) : null;
  const positive = diff != null ? diff >= 0 : null;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {before != null && (
        <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-mono"
          style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
          <DollarSign className="w-3 h-3" />{fmt(before)}
        </div>
      )}
      {before != null && after != null && <ArrowRight className="w-3.5 h-3.5" style={{ color: 'var(--text-tertiary)' }} />}
      {after != null && (
        <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-mono font-bold"
          style={{
            backgroundColor: positive ? 'color-mix(in srgb, var(--accent-success) 12%, transparent)' : 'color-mix(in srgb, var(--accent-danger) 12%, transparent)',
            color: positive ? 'var(--accent-success)' : 'var(--accent-danger)',
          }}>
          <DollarSign className="w-3 h-3" />{fmt(after)}
        </div>
      )}
      {diff != null && (
        <span className="text-xs font-bold px-2 py-0.5 rounded-full"
          style={{
            backgroundColor: positive ? 'color-mix(in srgb, var(--accent-success) 12%, transparent)' : 'color-mix(in srgb, var(--accent-danger) 12%, transparent)',
            color: positive ? 'var(--accent-success)' : 'var(--accent-danger)',
          }}>
          {positive ? '+' : ''}{fmt(diff)}
        </span>
      )}
    </div>
  );
}

// ─── Shared detail panel ──────────────────────────────────────────────────────
function DetailPanel({ l }) {
  const { t } = useLocale();
  const hasBalance = l.old_data?.balance != null || l.new_data?.balance != null;
  const hasStatus  = l.old_data?.status && l.new_data?.status;
  return (
    <div className="space-y-4">

      <div>
        <p className="text-[10px] uppercase tracking-wide font-semibold mb-1.5" style={{ color: 'var(--text-tertiary)' }}>{t('auditLog.performedBy')}</p>
        <div className="flex items-center gap-2 flex-wrap">
          <User className="w-3.5 h-3.5" style={{ color: 'var(--text-tertiary)' }} />
          <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{actorLabel(l)}</span>
          {l.performed_by_role && <RoleBadge role={l.performed_by_role} />}
        </div>
      </div>

      {l.new_data?.amount != null && (
        <div>
          <p className="text-[10px] uppercase tracking-wide font-semibold mb-1.5" style={{ color: 'var(--text-tertiary)' }}>{t('auditLog.amount')}</p>
          <span className="text-sm font-bold font-mono" style={{ color: 'var(--text-primary)' }}>
            {fmt(l.new_data.amount, l.new_data.currency)}
          </span>
        </div>
      )}

      {l.new_data?.pluginKey && (
        <div>
          <p className="text-[10px] uppercase tracking-wide font-semibold mb-1.5" style={{ color: 'var(--text-tertiary)' }}>{t('auditLog.paymentMethod')}</p>
          <span className="text-xs font-semibold px-2.5 py-1 rounded-lg"
            style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
            {formatMethod(l.new_data.pluginKey)}
          </span>
        </div>
      )}

      {hasBalance && (
        <div>
          <p className="text-[10px] uppercase tracking-wide font-semibold mb-1.5" style={{ color: 'var(--text-tertiary)' }}>{t('auditLog.balanceChange')}</p>
          <BalanceChange oldData={l.old_data} newData={l.new_data} />
        </div>
      )}

      {hasStatus && (
        <div>
          <p className="text-[10px] uppercase tracking-wide font-semibold mb-1.5" style={{ color: 'var(--text-tertiary)' }}>{t('auditLog.statusChange')}</p>
          <div className="flex items-center gap-2">
            <span className="text-xs px-2.5 py-1 rounded-full capitalize font-medium"
              style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
              {l.old_data.status.replace(/_/g, ' ')}
            </span>
            <ArrowRight className="w-3.5 h-3.5" style={{ color: 'var(--text-tertiary)' }} />
            <span className="text-xs px-2.5 py-1 rounded-full capitalize font-bold"
              style={{
                backgroundColor: ['approved', 'completed', 'proof_uploaded'].includes(l.new_data.status)
                  ? 'color-mix(in srgb, var(--accent-success) 15%, transparent)'
                  : l.new_data.status === 'rejected'
                    ? 'color-mix(in srgb, var(--accent-danger) 15%, transparent)'
                    : 'var(--bg-tertiary)',
                color: ['approved', 'completed', 'proof_uploaded'].includes(l.new_data.status)
                  ? 'var(--accent-success)'
                  : l.new_data.status === 'rejected'
                    ? 'var(--accent-danger)'
                    : 'var(--text-secondary)',
              }}>
              {l.new_data.status.replace(/_/g, ' ')}
            </span>
          </div>
        </div>
      )}

      {l.new_data?.review_notes && (
        <div className="px-3 py-2.5 rounded-lg"
          style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--accent-danger) 20%, transparent)' }}>
          <p className="text-[10px] uppercase tracking-wide font-semibold mb-0.5" style={{ color: 'var(--accent-danger)' }}>{t('auditLog.rejectionReason')}</p>
          <p className="text-xs italic" style={{ color: 'var(--accent-danger)' }}>{l.new_data.review_notes}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-3 pt-2 border-t text-[10px]"
        style={{ borderColor: 'var(--border-color)', color: 'var(--text-tertiary)' }}>
        <span>Log #{l.id}</span>
        {l.entity_id && <span>Ref #{l.entity_id}</span>}
        {l.entity_type && <span>{l.entity_type.replace(/_/g, ' ')}</span>}
        {l.ip_address && <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{l.ip_address}</span>}
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(l.created_at).toISOString()}</span>
      </div>
    </div>
  );
}

// ─── Mobile audit card ────────────────────────────────────────────────────────
function AuditCard({ l, expanded, onToggle }) {
  const { t } = useLocale();
  const isOpen = expanded === l.id;
  const cfg    = getActionConfig(l.action, l.entity_type, t);
  const Icon   = cfg.icon;
  return (
    <div className="rounded-2xl border overflow-hidden"
      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
      <button className="w-full flex items-start gap-3 px-4 py-3.5 text-left" onClick={() => onToggle(l.id)}>
        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ backgroundColor: `color-mix(in srgb, ${cfg.color} 15%, transparent)` }}>
          <Icon className="w-4 h-4" style={{ color: cfg.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold" style={{ color: cfg.color }}>{cfg.label}</p>
          <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{cfg.summary(l)}</p>
          <p className="text-[10px] mt-1" style={{ color: 'var(--text-tertiary)' }}>{new Date(l.created_at).toLocaleString()}</p>
        </div>
        <ChevronDown className="w-4 h-4 flex-shrink-0 mt-1 transition-transform duration-200"
          style={{ color: 'var(--text-tertiary)', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
      </button>
      {isOpen && (
        <div className="border-t px-4 py-3"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'color-mix(in srgb, var(--bg-tertiary) 40%, transparent)' }}>
          <DetailPanel l={l} />
        </div>
      )}
    </div>
  );
}

// ─── Mobile filter drawer ─────────────────────────────────────────────────────
function FilterDrawer({ activeTab, setActiveTab, search, setSearch, counts, onClose, onClear }) {
  const { t } = useLocale();
  const TABS = useTabs(t);
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative rounded-t-2xl pt-4 pb-8 z-10 max-h-[80vh] overflow-y-auto" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="w-10 h-1 rounded-full mx-auto mb-4" style={{ backgroundColor: 'var(--border-color)' }} />
        <div className="flex items-center justify-between px-4 mb-5">
          <h3 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{t('auditLog.filterAuditLog')}</h3>
          <button onClick={onClear} className="text-xs font-semibold flex items-center gap-1" style={{ color: 'var(--accent-primary)' }}>
            <X className="w-3.5 h-3.5" />{t('auditLog.reset')}
          </button>
        </div>
        <div className="px-4 space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-secondary)' }}>{t('common.search')}</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('auditLog.emailActionOrId')}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border focus:outline-none text-sm"
                style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--text-secondary)' }}>{t('auditLog.eventType')}</label>
            <div className="space-y-2">
              {TABS.map(tab => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm font-semibold transition-all"
                  style={{
                    backgroundColor: activeTab === tab.key ? 'color-mix(in srgb, var(--accent-primary) 12%, transparent)' : 'var(--bg-tertiary)',
                    borderColor: activeTab === tab.key ? 'color-mix(in srgb, var(--accent-primary) 30%, transparent)' : 'var(--border-color)',
                    color: activeTab === tab.key ? 'var(--accent-primary)' : 'var(--text-primary)',
                  }}>
                  {tab.label}
                  <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{counts[tab.key]}</span>
                </button>
              ))}
            </div>
          </div>
          <button onClick={onClose} className="w-full py-3 rounded-xl text-sm font-bold text-white"
            style={{ backgroundColor: 'var(--accent-primary)' }}>{t('auditLog.apply')}</button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function BrokerAuditLogPage() {
  const { t } = useLocale();
  const isMobile = useIsMobile();
  const TABS = useTabs(t);

  const [logs,        setLogs]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [activeTab,   setActiveTab]   = useState('all');
  const [search,      setSearch]      = useState('');
  const [expanded,    setExpanded]    = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await paymentsAPI.getAuditLog();
      setLogs(data);
    } catch (e) {
      setError(e.message || t('errors.general'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const filtered = logs.filter((l) => {
    if (!matchesTab(l, activeTab)) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        String(l.id).includes(q) ||
        (l.actor_email || '').toLowerCase().includes(q) ||
        (l.action      || '').toLowerCase().includes(q) ||
        String(l.entity_id || '').includes(q)
      );
    }
    return true;
  });

  const toggleExpanded = (id) => setExpanded(e => e === id ? null : id);
  const counts = {};
  TABS.forEach(tab => { counts[tab.key] = logs.filter(l => matchesTab(l, tab.key)).length; });

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE
  // ══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    if (loading) return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: 'var(--accent-primary)', borderTopColor: 'transparent' }} />
      </div>
    );

    return (
      <div className="flex flex-col -mx-4 -my-6 min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>

        <div className="px-4 pt-5 pb-6" style={{
          background: 'linear-gradient(135deg, var(--accent-primary) 0%, color-mix(in srgb, var(--accent-primary) 60%, #8b5cf6) 100%)'
        }}>
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.8)' }} />
            <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.6)' }}>{t('auditLog.title')}</p>
          </div>
          <p className="text-4xl font-black text-white leading-none font-mono">{counts.all}</p>
          <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>{t('auditLog.totalEventsRecorded')}</p>
          <div className="grid grid-cols-2 gap-2 mt-4">
            {[
              { label: t('auditLog.depositsTab'),    value: counts.deposits    },
              { label: t('auditLog.withdrawalsTab'), value: counts.withdrawals },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-xl px-3 py-2.5 text-center"
                style={{ backgroundColor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <p className="text-sm font-black text-white">{value}</p>
                <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 px-4 py-3 border-b"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('auditLog.searchPlaceholder')}
              className="w-full pl-9 pr-3 py-2 rounded-xl border text-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          </div>
          <button onClick={() => setShowFilters(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold"
            style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            <SlidersHorizontal className="w-4 h-4" />{TABS.find(tab => tab.key === activeTab)?.label}
          </button>
          <button onClick={fetchLogs} className="p-2 rounded-xl border"
            style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {activeTab !== 'all' && (
          <div className="flex items-center gap-2 px-4 py-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <div className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)', color: 'var(--accent-primary)' }}>
              {TABS.find(tab => tab.key === activeTab)?.label}
              <button onClick={() => setActiveTab('all')}><X className="w-2.5 h-2.5" /></button>
            </div>
          </div>
        )}

        {error && (
          <div className="mx-4 mt-3 flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs"
            style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 12%, transparent)', color: 'var(--accent-danger)', border: '1px solid color-mix(in srgb, var(--accent-danger) 25%, transparent)' }}>
            <AlertCircle className="w-4 h-4 shrink-0" />{error}
          </div>
        )}

        <div className="px-4 py-2 border-b" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
            {filtered.length} {filtered.length !== 1 ? t('auditLog.eventsPlural') : t('auditLog.events')}
          </p>
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <AlertCircle className="w-10 h-10 mb-3" style={{ color: 'var(--text-tertiary)' }} />
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('auditLog.noEventsMatch')}</p>
            <button onClick={() => setActiveTab('all')} className="mt-2 text-xs font-semibold" style={{ color: 'var(--accent-primary)' }}>
              {t('auditLog.showAll')}
            </button>
          </div>
        ) : (
          <div className="px-4 pt-3 pb-8 space-y-2">
            {filtered.map(l => <AuditCard key={l.id} l={l} expanded={expanded} onToggle={toggleExpanded} />)}
          </div>
        )}

        {showFilters && (
          <FilterDrawer activeTab={activeTab} setActiveTab={setActiveTab} search={search} setSearch={setSearch}
            counts={counts} onClose={() => setShowFilters(false)} onClear={() => { setActiveTab('all'); setSearch(''); }} />
        )}
        <div className="pb-[env(safe-area-inset-bottom)]" />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP
  // ══════════════════════════════════════════════════════════════════════════
  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-[var(--accent-primary)]" />
    </div>
  );

  return (
    <div className="space-y-6 p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-[var(--accent-primary)]" />
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">{t('auditLog.title')}</h1>
        </div>
        <button onClick={fetchLogs} className="px-4 py-2 border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-secondary)] text-sm flex items-center gap-1.5 text-[var(--text-primary)] transition-smooth">
          <RefreshCw className="w-3.5 h-3.5" />{t('common.refresh')}
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />{error}
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        {[
          { label: t('auditLog.totalEvents'),      value: counts.all,         color: 'text-[var(--text-primary)]'                      },
          { label: t('auditLog.depositEvents'),    value: counts.deposits,    color: 'text-blue-600 dark:text-blue-400'                },
          { label: t('auditLog.withdrawalEvents'), value: counts.withdrawals, color: 'text-yellow-600 dark:text-yellow-400'            },
        ].map(c => (
          <div key={c.label} className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg p-4">
            <p className="text-xs text-[var(--text-secondary)]">{c.label}</p>
            <p className={`text-2xl font-bold mt-0.5 ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-smooth ${activeTab === tab.key ? 'bg-[var(--accent-primary)] text-white' : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]'}`}>
              {tab.label} <span className="opacity-70">({counts[tab.key]})</span>
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]" />
          <input type="text" placeholder={t('auditLog.emailActionOrId')} value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-sm focus:border-[var(--accent-primary)] focus:outline-none text-[var(--text-primary)] placeholder-[var(--text-tertiary)]" />
        </div>
      </div>

      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg text-[var(--text-secondary)]">
            <AlertCircle className="w-8 h-8" />
            <p className="text-sm">{t('auditLog.noEntriesMatch')}</p>
          </div>
        ) : filtered.map(l => {
          const isOpen = expanded === l.id;
          const cfg    = getActionConfig(l.action, l.entity_type, t);
          const Icon   = cfg.icon;
          return (
            <div key={l.id} className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl overflow-hidden">
              <button type="button" onClick={() => toggleExpanded(l.id)}
                className="w-full flex items-start gap-4 px-5 py-4 text-left hover:bg-[var(--bg-secondary)] transition-smooth">
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: `color-mix(in srgb, ${cfg.color} 15%, transparent)` }}>
                  <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm font-bold" style={{ color: cfg.color }}>{cfg.label}</span>
                    <div className="flex items-center gap-1.5 text-xs flex-shrink-0" style={{ color: 'var(--text-tertiary)' }}>
                      <Clock className="w-3 h-3" />{new Date(l.created_at).toLocaleString()}
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--text-primary)' }}>{cfg.summary(l)}</p>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    {l.performed_by_role && <RoleBadge role={l.performed_by_role} />}
                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{actorLabel(l)}</span>
                    {l.entity_id && (
                      <span className="text-xs font-mono px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-tertiary)' }}>
                        Ref #{l.entity_id}
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-[var(--text-tertiary)] flex-shrink-0 mt-1">
                  {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </span>
              </button>
              {isOpen && (
                <div className="border-t px-5 py-4" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
                  <DetailPanel l={l} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}